import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

export type Question = components["schemas"]["Question"];
export type InboxItem = components["schemas"]["QuestionInboxItem"];
export type StaffResponse = components["schemas"]["StaffResponse"];
export type DeliveryAction = components["schemas"]["DeliveryAction"];

export class QuestionRequestError extends Error {
  constructor(public status: number) {
    super(
      status === 409
        ? "This question changed. Review the latest saved answer before continuing."
        : status === 404
          ? "This question is no longer available to you."
          : "Your response could not be confirmed. Retry to check or save the same response.",
    );
  }
}

export const fetchInbox = (after?: string) =>
  request(() =>
    apiClient.GET("/questions/inbox", {
      params: { query: { after, limit: 20 } },
    }),
  );

export const fetchQuestion = (id: string) =>
  request(() =>
    apiClient.GET("/questions/{question_uuid}", {
      params: { path: { question_uuid: id } },
    }),
  );

export async function respond(
  id: string,
  body: StaffResponse | DeliveryAction,
) {
  const params = { path: { question_uuid: id } };
  const result =
    "expected_revision" in body
      ? await apiClient.POST("/questions/{question_uuid}/responses", {
          params,
          body,
        })
      : await apiClient.POST("/questions/{question_uuid}/delivery-events", {
          params,
          body,
        });
  if (!result.data) throw new QuestionRequestError(result.response.status);
  return result.data;
}

export const inboxItemContext = (item: InboxItem): string =>
  [item.room_name, ...(item.patient_names ?? [])].filter(Boolean).join(" · ") ||
  "Clinic question";

export function answerText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  if (
    typeof value === "object" &&
    "label" in value &&
    typeof value.label === "string"
  )
    return value.label;
  return JSON.stringify(value);
}

export function candidatesOf(
  value: unknown,
): { rank: number; label: string }[] {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((item: unknown) => {
      if (
        !item ||
        typeof item !== "object" ||
        !("rank" in item) ||
        !("label" in item) ||
        typeof item.rank !== "number" ||
        typeof item.label !== "string"
      )
        return [];
      return [{ rank: item.rank, label: item.label }];
    })
    .sort((a, b) => a.rank - b.rank);
}
